import React, { useMemo, useState } from 'react';
import { Sparkles, Zap } from 'lucide-react';
import { ForecastAiProposalCard } from '../ForecastWorkspace';
import CorporateProjectCitationChip from './CorporateProjectCitationChip';
import CorporateProjectPolicyBadge from './CorporateProjectPolicyBadge';
import CorporateProjectPolicyPanel from './CorporateProjectPolicyPanel';

const HINT_TONES = {
  capital: 'gold',
  operating: 'neutral',
  unknown: 'yellow',
  marginal: 'green',
  existing: 'red'
};

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'edited', label: 'Edited' },
  { key: 'rejected', label: 'Rejected' }
];
const TYPE_TABS = [
  { key: 'all', label: 'All types' },
  { key: 'marginality', label: 'Revenue' },
  { key: 'expense', label: 'Opex' },
  { key: 'capitalization', label: 'Capex' }
];
const CONFIDENCE_TABS = [
  { key: 0, label: 'Any confidence' },
  { key: 0.7, label: '≥ 70%' },
  { key: 0.85, label: '≥ 85%' }
];

// D2: a skipped AI reviewer never ran — never present it as a completed review.
const reviewerChip = (aiDraft) => {
  const verdict = aiDraft?.review?.verdict;
  if (!verdict || verdict === 'skipped') {
    return { tone: 'neutral', label: 'Reviewer not run' };
  }
  return {
    tone: verdict === 'approve' ? 'green' : 'yellow',
    label: `Review: ${verdict} · ${aiDraft.revisions || 0} revision(s)`
  };
};

const CorporateProjectExtractStage = ({
  activeCitation,
  aiDraft,
  busy,
  citationById,
  editable,
  extraction,
  guidance,
  hasSpread,
  isPdfSource,
  onAcceptProposal,
  onEditProposal,
  onGuidanceChange,
  onOpenCitation,
  onRejectProposal,
  onBulkAccept,
  onRunDraft,
  onStartManualSpread,
  proposalQueue,
  pendingProposalCount
}) => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');
  const [confidenceFilter, setConfidenceFilter] = useState(0);

  const counts = useMemo(() => {
    const base = { pending: 0, accepted: 0, edited: 0, rejected: 0, manual: 0 };
    (proposalQueue || []).forEach((proposal) => {
      base[proposal.status] = (base[proposal.status] || 0) + 1;
    });
    return base;
  }, [proposalQueue]);

  const filtered = useMemo(() => (proposalQueue || []).filter((proposal) => {
    if (statusFilter !== 'all' && proposal.status !== statusFilter) return false;
    if (typeFilter !== 'all' && proposal.type !== typeFilter) return false;
    if (confidenceFilter > 0) {
      const confidence = Number(proposal.confidence);
      if (!Number.isFinite(confidence) || confidence < confidenceFilter) return false;
    }
    return true;
  }), [proposalQueue, statusFilter, typeFilter, confidenceFilter]);

  const bulkEligibleCount = useMemo(
    () => (proposalQueue || []).filter((proposal) =>
      proposal.status === 'pending'
      && proposal.lowRiskEligible
      && Number(proposal.confidence) >= 0.85).length,
    [proposalQueue]
  );

  const chip = reviewerChip(aiDraft);

  const citationChip = (figureId) => (
    <CorporateProjectCitationChip
      activeCitation={activeCitation}
      citation={figureId ? citationById[figureId] : null}
      isPdfSource={isPdfSource}
      onOpen={onOpenCitation}
    />
  );

  return (
    <section className="forecast-panel workspace-stage-panel" id="project-stage-extract" tabIndex="-1">
      <div className="forecast-panel-head">
        <span className="forecast-section-code">PRJ_02 ● Extract &amp; classify</span>
        <h2>Verify the grounded claims before generating the financial spread</h2>
      </div>
      {extraction && (
        <div className="project-extraction">
          <div className="project-extraction-meta">
            <div><span>Project</span><strong>{extraction.projectName || '—'}</strong></div>
            <div><span>Sponsor</span><strong>{extraction.sponsor || '—'}</strong></div>
            <div><span>Department</span><strong>{extraction.department || '—'}</strong></div>
            <div><span>Start / duration</span><strong>{extraction.requestedStart || '—'} · {extraction.durationMonths || '—'} mo</strong></div>
            <div><span>Stated budget</span><strong>{extraction.totalBudgetMM ? `$${extraction.totalBudgetMM.toFixed(1)}MM` : '—'}</strong></div>
          </div>
          <p className="forecast-submit-note">{extraction.summary}</p>
          {aiDraft && (
            <div className="forecast-subpanel" aria-live="polite">
              <div className="forecast-panel-head">
                <div>
                  <span className="forecast-section-code">AI proposal review</span>
                  <h3>Accept, edit, or reject each proposed financial classification</h3>
                </div>
                <span className={`forecast-health-chip ${(pendingProposalCount ?? counts.pending) > 0 ? 'yellow' : 'green'}`}>
                  {(pendingProposalCount ?? counts.pending)} pending
                </span>
              </div>

              {/* Count summary + filter toolbar. */}
              <div className="forecast-submit-chips" style={{ marginBottom: 8 }}>
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`forecast-status-chip ${statusFilter === tab.key ? 'moss' : 'neutral'}`}
                    aria-pressed={statusFilter === tab.key}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setStatusFilter(tab.key)}
                  >
                    {tab.label}{tab.key !== 'all' && counts[tab.key] ? ` (${counts[tab.key]})` : ''}
                  </button>
                ))}
              </div>
              <div className="forecast-submit-chips" style={{ marginBottom: 8 }}>
                {TYPE_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`forecast-health-chip mini ${typeFilter === tab.key ? 'gold' : 'neutral'}`}
                    aria-pressed={typeFilter === tab.key}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setTypeFilter(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
                {CONFIDENCE_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`forecast-health-chip mini ${confidenceFilter === tab.key ? 'gold' : 'neutral'}`}
                    aria-pressed={confidenceFilter === tab.key}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setConfidenceFilter(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {editable && onBulkAccept && (
                <div className="forecast-submit-chips" style={{ marginBottom: 8 }}>
                  <button
                    type="button"
                    className="forecast-secondary-button compact"
                    disabled={busy === 'bulk-accept' || bulkEligibleCount === 0}
                    onClick={() => onBulkAccept()}
                    title="Accept all pending proposals with confidence ≥ 85% and no policy risk flags"
                  >
                    <Zap size={14} aria-hidden="true" />
                    {busy === 'bulk-accept'
                      ? 'Accepting…'
                      : `Bulk-accept ${bulkEligibleCount} low-risk`}
                  </button>
                  <span className="forecast-submit-note" style={{ margin: 0 }}>
                    Low-risk = confidence ≥ 85% and no policy flags; other proposals still need a manual decision.
                  </span>
                </div>
              )}

              {filtered.length === 0 ? (
                <p className="workspace-empty-state">
                  {counts.pending === 0
                    ? 'All generated classifications have an explicit reviewer decision.'
                    : 'No proposals match the current filters.'}
                </p>
              ) : filtered.map((proposal) => (
                <ForecastAiProposalCard
                  key={proposal.proposalId || `${proposal.collection}-${proposal.id}`}
                  proposal={proposal}
                  onAccept={editable && proposal.status === 'pending' ? onAcceptProposal : undefined}
                  onEdit={editable ? onEditProposal : undefined}
                  onReject={editable && proposal.status !== 'rejected' ? onRejectProposal : undefined}
                >
                  {citationChip(proposal.sourceFigureId)}
                  <CorporateProjectPolicyBadge
                    citation={proposal.policyCitation}
                    stage={proposal.policyStage}
                    confidence={proposal.policyConfidence}
                    classification={proposal.policyClassification}
                  />
                </ForecastAiProposalCard>
              ))}
            </div>
          )}

          <div className="forecast-submit-chips" style={{ marginTop: 12 }}>
            <label className="project-ai-guidance">
              <span>Optional AI guidance</span>
              <input
                type="text"
                name="project-ai-guidance"
                autoComplete="off"
                className="op3-note wide"
                placeholder="Add constraints or context for the first pass…"
                value={guidance}
                onChange={(event) => onGuidanceChange(event.target.value)}
              />
            </label>
            <button type="button" className="forecast-primary-button" disabled={busy === 'draft' || !editable} onClick={onRunDraft}>
              <Sparkles size={15} aria-hidden="true" />
              {busy === 'draft' ? 'Generating spread…' : aiDraft ? 'Regenerate spread' : 'Generate spread'}
            </button>
            {!aiDraft && !hasSpread && (
              <button type="button" className="forecast-secondary-button" disabled={!editable} onClick={onStartManualSpread}>
                Start manual spread
              </button>
            )}
            {aiDraft && (
              <span
                className={`forecast-health-chip ${chip.tone}`}
                title={(aiDraft.review?.checks || []).map((check) => `${check.passed ? '✓' : '✗'} ${check.name}: ${check.note}`).join('\n')}
              >
                {chip.label}
              </span>
            )}
          </div>

          <details className="workspace-change-drawer">
            <summary>
              Source evidence and extracted risks
              <span>{(extraction.spendItems || []).length + (extraction.revenueClaims || []).length}</span>
            </summary>
            <div className="workspace-change-content">
              {(extraction.risks || []).length > 0 && (
                <div className="forecast-subpanel" aria-label="Extracted project risks">
                  <h3>Risks identified in the source</h3>
                  <ul className="forecast-submit-note">
                    {extraction.risks.map((risk, index) => <li key={`${risk}-${index}`}>{risk}</li>)}
                  </ul>
                </div>
              )}
              <div className="project-claims-grid">
                <div>
                  <h4>Spend items (capital hint)</h4>
                  {(extraction.spendItems || []).map((item, index) => (
                    <div className="project-claim-row" key={index}>
                      <span className={`forecast-health-chip ${HINT_TONES[item.capitalHint]} mini`}>{item.capitalHint}</span>
                      <span>
                        {item.description} {citationChip(item.figureId)}
                        <CorporateProjectPolicyBadge
                          citation={item.policyCitation}
                          stage={item.stage}
                          confidence={item.confidence}
                          classification={item.classification}
                        />
                        {(item.policyFlags || []).map((flag) => (
                          <span key={flag} className="forecast-health-chip yellow mini" title={flag}>{flag.replace(/_/g, ' ')}</span>
                        ))}
                      </span>
                      <strong className="forecast-mono">${Number(item.amountMM).toFixed(2)}MM</strong>
                    </div>
                  ))}
                </div>
                <div>
                  <h4>Revenue claims (marginality hint)</h4>
                  {(extraction.revenueClaims || []).map((claim, index) => (
                    <div className="project-claim-row" key={index}>
                      <span className={`forecast-health-chip ${HINT_TONES[claim.marginalityHint]} mini`}>{claim.marginalityHint}</span>
                      <span>{claim.description} {citationChip(claim.figureId)}</span>
                      <strong className="forecast-mono">${Number(claim.amountMM).toFixed(2)}MM</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </details>

          <CorporateProjectPolicyPanel />
        </div>
      )}
    </section>
  );
};

export default CorporateProjectExtractStage;
